import { memo, useCallback, useMemo, useRef, ReactElement } from 'react';
import { EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { Button, Flex, Space, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';

import { Table, TableComponent, TableProps } from '@ohpen/enso-ui';
import { Guard } from '@ohpen/react-authz';
import { ActionEntityType, ProcurationFeeItem, ProcurationFeeItemsResponse } from '@pmp/api';
import { lockActionTitle } from '@pmp/utils';
import { ListProps } from '@pmp/types';

import { procurationFeeColumns, procurationFeeFeesColumns, procurationFeeFlatColumns } from '../tableConfig';
import { ProcurationFeesExport } from '../interfaces';

const entityType = ActionEntityType.PROCURATION_FEES;

export const ProcurationFeesList = memo(
  ({ data, isFetching, valueLists, onCreate, onView, onEdit, onDelete }: ListProps<ProcurationFeeItemsResponse>): ReactElement => {
    const tableRef = useRef<TableComponent | null>(null);
    const { t } = useTranslation();

    const getRowActions: (record: ProcurationFeeItemsResponse) => ReactElement[] = useCallback(
      record => {
        const rowActions = [
          <Tooltip key="view" title={t('global.buttons.view')} placement="top">
            <Button type="text" aria-label="View Procuration Fee" icon={<EyeOutlined />} onClick={() => onView(record)} />
          </Tooltip>,
          <Guard key="edit" permissions={['procurationFees:update']} flags={['enableProcurationFeesCrud']}>
            <Tooltip title={lockActionTitle('edit', record.lockedByActionId)} placement="top">
              <Button
                type="text"
                aria-label="Edit Procuration Fee"
                icon={<EditOutlined key="edit" />}
                onClick={() => onEdit(record)}
                disabled={!!record.lockedByActionId}
              />
            </Tooltip>
          </Guard>,
          <Guard key="delete" permissions={['procurationFees:delete']} flags={['enableProcurationFeesCrud']}>
            <Tooltip title={lockActionTitle('delete', record.lockedByActionId)} placement="top">
              <Button
                type="text"
                aria-label="Delete Procuration Fee"
                icon={<DeleteOutlined key="delete" />}
                onClick={() => onDelete(record)}
                disabled={!!record.lockedByActionId}
              />
            </Tooltip>
          </Guard>,
        ];

        return rowActions;
      },
      [onDelete, onEdit, onView, t],
    );

    const tableProps: TableProps<
      ActionEntityType,
      ProcurationFeeItemsResponse,
      ProcurationFeeItem,
      ProcurationFeeItemsResponse,
      ProcurationFeesExport
    > = useMemo(
      () => ({
        id: entityType,
        itemsLabel: t('lender.procurationFees.labels.procurationFee'),
        ref: tableRef,
        dataSource: data ?? [],
        columns: procurationFeeColumns,
        exportColumns: procurationFeeFlatColumns,
        rowKey: 'id',
        pagination: true,
        scroll: { x: 'max-content', y: '100%' },
        valueLists,
        loading: isFetching,
        getRowActions,
        expanded: {
          columns: procurationFeeFeesColumns,
          columnsKey: 'feeItems',
          rowKey: 'index',
        },
        showViews: false,
      }),
      [t, data, valueLists, isFetching, getRowActions],
    );

    return (
      <Table {...tableProps}>
        <Table.ActionsBar>
          <Flex justify="space-between">
            <Space size="middle">
              <Table.GlobalSearch />
            </Space>
            <Space size="middle">
              <Table.ResetFiltersButton />
              <Table.SelectColumnsButton />
              <Guard permissions={['procurationFees:create']} flags={['enableProcurationFeesCrud']}>
                <Table.CreateButton onClick={onCreate} />
              </Guard>
            </Space>
          </Flex>
        </Table.ActionsBar>
      </Table>
    );
  },
);
